import { ContactInformation } from './../../../../models/masters/contact-information.interface';
import { DynamicFormComponent } from './../../../dynamic-form/dynamic-form.component';
import { DynamicForm } from './../../../../models/dynamic-form';
import { Component, ElementRef, OnInit, OnDestroy, QueryList, ViewChildren, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder } from "@angular/forms";
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../../services/api.service';
import { ToasterService } from '../../../../services/toaster.service';
import { LoadingService } from '../../../../services/loading.service';
import { Location } from '../../../../models/location-master.interface';
import { SharedModule } from '../../../../modules/shared/shared.module';
import { SvgIconComponent } from '../../../svg-icon/svg-icon.component';
import { BackButtonComponent } from '../../../back-button/back-button.component';
import { MatSelectModule } from '@angular/material/select';
import { AutocompleteDropdownComponent } from '../../../autocomplete-dropdown/autocomplete-dropdown.component';
import locationFormConfig from "../config/LocationConfigNew.json"
import { DynamicFormService } from '../../../../services/dynamic-form.service';
import { NgFor,NgForOf } from '@angular/common';

import { PageCloseButtonComponent } from "../../../common/close-button/page-close-button.component";

import contactConfig from "../config/location-contact-config.json";
import { CONTACT_ACTIONS, ContactActions, ContactService } from '../../../../services/masters/contact.service';
import { ContactInformationComponent } from '../../contact-information/contact-info/contact-information.component';
import { SectionHeaderComponent } from '../../../common/section-header/section-header.component';

@Component({
  selector: 'app-manage-location',
  standalone: true,
  imports: [
    DynamicFormComponent,
    SharedModule,
    SvgIconComponent,
    BackButtonComponent,
    MatSelectModule,
    AutocompleteDropdownComponent,
    NgFor,
    NgForOf,
    PageCloseButtonComponent,
    ContactInformationComponent,
    SectionHeaderComponent
],
  templateUrl: './manage-location.component.html',
  styleUrls: ['./manage-location.component.scss'],
})
export class ManageLocationComponent implements OnInit, OnDestroy {
  masterForm!: FormGroup;
  dynamicForm!: DynamicForm;
  basicForm!: DynamicForm;
  contactConfig!: DynamicForm;
  personFormConfig!: DynamicForm;

  readOnlyMode = true;
  masterEditingEnabled = false;
  contactAction: ContactActions = CONTACT_ACTIONS.ADD;
  contactInformation!: ContactInformation;

  masterId: string = '';
  locationTypes = [
    { name: 'Port', value: 'PORT', checked: false },
    {
      name: 'Inland Container Depot',
      value: 'INLAND_CONTAINER_DEPOT',
      checked: false,
    },
    { name: 'CFS', value: 'CFS', checked: false },
    { name: 'Empty Depot', value: 'EMPTY_DEPOT', checked: false },
    { name: 'Berthing Point', value: 'BERTHING_POINT', checked: false },
    { name: 'Port Terminal', value: 'PORT_TERMINAL', checked: false },
    { name: 'Repairing Yard', value: 'REPAIRING_YARD', checked: false },
  ];
  
  @ViewChildren('formField') formFields!: QueryList<ElementRef>;
  @ViewChild(ContactInformationComponent) contactInfoComponent!: ContactInformationComponent;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private toasterService: ToasterService,
    private loadingService: LoadingService,
    private router: Router,
    private route: ActivatedRoute,
    private dynamicFormService: DynamicFormService,
    private contactService: ContactService
  ) {
    this.masterForm = fb.group({});
    this.contactConfig = contactConfig as DynamicForm;
  }

  ngOnInit(): void {
    this.dynamicForm = locationFormConfig.basicFormElements;

    this.dynamicFormService.addControlsToForm(this.masterForm, this.dynamicForm);

    this.masterId = this.route.snapshot.paramMap.get('locationId') || '';
    this.setupComponentMode();
    if (this.masterId) {
      this.loadLocationData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupComponentMode(): void {
    if (this.masterId) {
      const path = this.route.snapshot.url[0].path;
      this.masterEditingEnabled = path === 'edit';
      this.readOnlyMode = !this.masterEditingEnabled;
      this.contactAction = CONTACT_ACTIONS.EDIT;
      if (this.readOnlyMode) {
        this.masterForm.disable();
        this.contactAction = CONTACT_ACTIONS.VIEW;
      }
    } else {
      this.readOnlyMode = false;
      this.masterEditingEnabled = false;
    }
  }

  private loadLocationData(): void {
    this.loadingService.show();
    this.api
      .getLocation(this.masterId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const locationData: Location = res.data;
          this.patchFormWithLocationData(locationData);
          this.loadingService.hide();
        },
        error: (err) => {
          console.error('Error loading location data:', err);
          this.toasterService.error('Failed to load location data');
          this.loadingService.hide();
        },
      });
  }

  private patchFormWithLocationData(locationData: Location): void {
    this.masterForm.patchValue({
      ...locationData,
    });
    this.contactInformation = locationData.contactInformation;
  }

  submitMasterForm(): void {
    this.masterForm.markAllAsTouched();
    this.contactInfoComponent.contactForm.markAllAsTouched();
    if (!this.contactInfoComponent.contactForm.valid || !this.masterForm.valid) {
      return;
    }

    const formValue = this.masterForm.value;
    const masterData: Location = this.restructureFormData(formValue);

    this.loadingService.show();
    const action$ = this.masterEditingEnabled
      ? this.api.updateLocation(masterData, this.masterId)
      : this.api.addLocation(masterData);

    action$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        const message = this.masterEditingEnabled
          ? `Location ${masterData.name} has been updated!`
          : `Location ${masterData.name} has been added!`;
        this.toasterService.success(message);
        this.loadingService.hide();
        this.goBack();
      },
      error: (err) => {
        this.toasterService.error(err.error.errorDesc || 'An error occurred');
        console.error('Error submitting form:', err);
        this.loadingService.hide();
      },
    });
  }

  private restructureFormData(formValue: any): Location {
    const contactInformation = this.contactService.createContactFromForm(this.contactInfoComponent.contactForm);
    return {
      ...formValue,
      contactInformation,
    };
  }

  scrollToFirstInvalidControl(): void {
    const firstInvalidControl = this.formFields.find((element) => {
      const nativeElement = element.nativeElement as HTMLElement;
      return nativeElement.classList.contains('ng-invalid');
    });

    if (firstInvalidControl) {
      firstInvalidControl.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      (firstInvalidControl.nativeElement as HTMLElement).focus();
    }
  }

  goBack(): void {
    this.router.navigateByUrl('/manage-masters/locations');
  }
}
